import { Injectable } from '@angular/core';
import { combineLatest, Observable, of } from 'rxjs';
import { map, shareReplay } from 'rxjs/operators';
import { RxTemplateService } from '@shared/rx-templates/rx-template.service';
import { RxTemplate } from '@shared/rx-templates/rx-template.model';
import { ProcedureMap } from '@shared/models/procedure-map';
import { OrderQuery } from '@modules/order/state/order-query';
import { DentureDetailsUtils } from './utils/denture-details-utils';
import { IDentureVisibilityService } from './interfaces/denture-visibility';

/** Minimum field-level visibility contract shared by v1 and intuitive denture visibility services. */
export interface DentureBaseFieldsVisibility {
	stage: boolean;
	mould: boolean;
	shadeSystem: boolean;
	teethShade: boolean;
	gingival: boolean;
}

/** Field-level visibility flags for the v1 denture-details section */
export type DentureDetailsFieldsVisibility = DentureBaseFieldsVisibility;

@Injectable({
	providedIn: 'root'
})
export class DentureDetailsVisibilityService implements IDentureVisibilityService {
	readonly fields$: Observable<DentureDetailsFieldsVisibility> = this.rxTemplateService.template$.pipe(
		map(template => DentureDetailsVisibilityService.fieldsFromTemplate(template)),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	private readonly isSectionNonEmpty$: Observable<boolean> = this.fields$.pipe(
		map(f => DentureDetailsVisibilityService.isSectionNonEmpty(f))
	);

	/** True when the denture section should be shown (procedure is Denture AND section has visible fields). */
	readonly isSectionVisible$: Observable<boolean> = combineLatest([this.orderQuery.procedureMap$, this.isSectionNonEmpty$]).pipe(
		map(([procedureMap, nonEmpty]) => DentureDetailsUtils.isDentureDetailsEnabled(procedureMap?.ProcedureId) && nonEmpty),
		shareReplay({ bufferSize: 1, refCount: true })
	);

	static fieldsFromTemplate(template: Readonly<RxTemplate>): DentureDetailsFieldsVisibility {
		return {
			stage: !template.ui.denture?.items.stage.disabled,
			mould: !template.ui.denture?.items.mould.disabled,
			shadeSystem: !template.ui.denture?.items.shadeSystem.disabled,
			teethShade: !template.ui.denture?.items.teethShade.disabled,
			gingival: !template.ui.denture?.items.gingival.disabled
		};
	}

	static isSectionNonEmpty(f: DentureBaseFieldsVisibility): boolean {
		return f.stage || f.mould || f.shadeSystem || f.teethShade || f.gingival;
	}

	constructor(
		private readonly rxTemplateService: RxTemplateService,
		private readonly orderQuery: OrderQuery
	) {}

	fieldsFor$(targetTemplate: Readonly<RxTemplate>, targetProcedureMap: ProcedureMap | null): Observable<DentureDetailsFieldsVisibility> {
		// If the target procedure map is not a denture procedure, all fields will be hidden.
		if (!DentureDetailsUtils.isDentureDetailsEnabled(targetProcedureMap?.ProcedureId)) {
			return of({ stage: false, mould: false, shadeSystem: false, teethShade: false, gingival: false });
		}

		return of(DentureDetailsVisibilityService.fieldsFromTemplate(targetTemplate));
	}
}
